import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import classNames from 'classnames'
import {
  type ActionItem,
  CalendarDropdown,
  FullPageWrapper,
  Icon,
  SpinnerLoader,
  Tooltip,
  Typography,
} from 'shared/ui'
import { LINK_CREDIT_USAGE_LEARN_MORE } from 'shared/constants/links'
import { CREDIT_USAGE_METRICS_CONFIG } from 'entities/Analytics/lib/constants'
import { ContactsDetails } from 'widgets/ContactsDetails'
import {
  CreditUsageAnalyticsPageProvider,
  useCreditUsageAnalyticsPageContext,
} from 'pages/analytics/pages/creditUsage/context/creditUsageAnalyticsPageContext'
import { AnalyticsEmptyState, AnalyticsHeader } from 'pages/analytics/ui'

import { ChartFilter } from 'pages/analytics/ui/ChartFilter'
import styles from './styles.module.scss'
import { CreditUsageBarChart, CreditUsageLineChart } from './Chart'
import { CreditUsageTable } from './CreditUsageTable/CreditUsageTable'

export const CreditUsageAnalyticsPageContent = observer(() => {
  const {
    calendarFilterStore,
    chartFilterStore,
    chartDataStore,
    pageLoading,
    chartLoading,
    openExportModal,
    contactsDetailsStore,
  } = useCreditUsageAnalyticsPageContext()
  const { getStatisticByKey, hasStatistic } = chartDataStore

  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set([]))

  const handleToggleMetric = (metricKey: string) => {
    setHiddenMetrics((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(metricKey)) {
        newSet.delete(metricKey)
      } else {
        newSet.add(metricKey)
      }
      return newSet
    })
  }

  const totalStat = getStatisticByKey('total_credits')
  const transcriptsStat = getStatisticByKey('transcripts_credits')
  const agentsStat = getStatisticByKey('chatbots_credits')
  const callingAgentsStat = getStatisticByKey('ai_calling_agent_credits')
  const callsStat = getStatisticByKey('calls_credits')
  const messagesStat = getStatisticByKey('messages_credits')
  const ringlessVoicemailStat = getStatisticByKey('rvm_credits')

  const exportActions: ActionItem[] = [
    {
      text: 'Export data',
      icon: 'download',
      onAction: openExportModal,
    },
  ]

  const renderContent = () => {
    if (pageLoading) {
      return <SpinnerLoader ariaLabel={'CreditUsageAnalyticsPage'} />
    }

    if (!hasStatistic) {
      return <AnalyticsEmptyState />
    }

    return (
      <>
        <div className={styles.wrapKpiSection}>
          <div className={styles.kpiSection}>
            <div className={classNames(styles.kpiCard, styles.kpiTotalCredits)}>
              <div className={styles.kpiColorBar} />
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiItemHeader}>
                  <Typography
                    variant={'body-rg-regular'}
                    color={'tertiary'}
                    ariaLabel={'TotalCreditsTitle'}
                  >
                    Total credits
                  </Typography>
                </div>
                <Typography
                  variant={'heading-lg'}
                  color={'primary'}
                  ariaLabel={'TotalCreditsValue'}
                >
                  {totalStat?.value?.toLocaleString() || '0'}
                </Typography>
              </div>
            </div>
            <div className={styles.kpiCard} onClick={() => handleToggleMetric('messages_credits')}>
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('messages_credits')
                    ? 'var(--gray-10)'
                    : CREDIT_USAGE_METRICS_CONFIG.messages_credits.color,
                }}
              />
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiItemHeader}>
                  <Typography
                    variant={'body-rg-regular'}
                    color={'tertiary'}
                    ariaLabel={'MessagesCreditsTitle'}
                  >
                    Messages credits
                  </Typography>
                  <Tooltip
                    placement='top'
                    breakWord
                    label={[
                      <Typography
                        key={'messages_credits_tooltip'}
                        variant={'body-md-regular'}
                        ariaLabel={'MessagesCreditsTooltip'}
                      >
                        1 credit per SMS segment, 2 credits per MMS.
                        <Typography
                          key={'link'}
                          variant={'body-md-medium'}
                          linkLight
                          ariaLabel={'LearnMore'}
                        >
                          <a href={LINK_CREDIT_USAGE_LEARN_MORE} target={'_blank'} rel='noreferrer'>
                            &nbsp; Learn more.
                          </a>
                        </Typography>
                      </Typography>,
                    ]}
                  >
                    <Icon icon='question' fontSize={14} color={'var(--content-neutral-primary)'} />
                  </Tooltip>
                </div>
                <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'TotalValue'}>
                  {messagesStat?.value?.toLocaleString() || '0'}
                </Typography>
              </div>
            </div>
            <div className={styles.kpiCard} onClick={() => handleToggleMetric('calls_credits')}>
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('calls_credits')
                    ? 'var(--gray-10)'
                    : CREDIT_USAGE_METRICS_CONFIG.calls_credits.color,
                }}
              />
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiItemHeader}>
                  <Typography
                    variant={'body-rg-regular'}
                    color={'tertiary'}
                    ariaLabel={'CallsCreditsTitle'}
                  >
                    Calls credits
                  </Typography>
                </div>
                <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'CallsValue'}>
                  {callsStat?.value?.toLocaleString() || '0'}
                </Typography>
              </div>
            </div>
            <div className={styles.kpiCard} onClick={() => handleToggleMetric('chatbots_credits')}>
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('chatbots_credits')
                    ? 'var(--gray-10)'
                    : CREDIT_USAGE_METRICS_CONFIG.chatbots_credits.color,
                }}
              />
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiItemHeader}>
                  <Typography
                    variant={'body-rg-regular'}
                    color={'tertiary'}
                    ariaLabel={'AgentCreditsTitle'}
                  >
                    Texting agent credits
                  </Typography>
                </div>
                <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'AgentValue'}>
                  {agentsStat?.value?.toLocaleString() || '0'}
                </Typography>
              </div>
            </div>

            <div
              className={styles.kpiCard}
              onClick={() => handleToggleMetric('ai_calling_agent_credits')}
            >
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('ai_calling_agent_credits')
                    ? 'var(--gray-10)'
                    : CREDIT_USAGE_METRICS_CONFIG.ai_calling_agent_credits.color,
                }}
              />
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiItemHeader}>
                  <Typography
                    variant={'body-rg-regular'}
                    color={'tertiary'}
                    ariaLabel={'CallingAgentCreditsTitle'}
                  >
                    Calling agent credits
                  </Typography>
                </div>
                <Typography
                  variant={'heading-lg'}
                  color={'primary'}
                  ariaLabel={'CallingAgentValue'}
                >
                  {callingAgentsStat?.value?.toLocaleString() || '0'}
                </Typography>
              </div>
            </div>

            <div
              className={styles.kpiCard}
              onClick={() => handleToggleMetric('transcripts_credits')}
            >
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('transcripts_credits')
                    ? 'var(--gray-10)'
                    : CREDIT_USAGE_METRICS_CONFIG.transcripts_credits.color,
                }}
              />
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiItemHeader}>
                  <Typography
                    variant={'body-rg-regular'}
                    color={'tertiary'}
                    ariaLabel={'TranscriptsTitle'}
                  >
                    Transcripts credits
                  </Typography>
                </div>
                <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'TranscriptsValue'}>
                  {transcriptsStat?.value?.toLocaleString() || '0'}
                </Typography>
              </div>
            </div>
            <div
              className={classNames(styles.kpiCard, styles.kpiRvmCredits)}
              onClick={() => handleToggleMetric('rvm_credits')}
            >
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('rvm_credits')
                    ? 'var(--gray-10)'
                    : CREDIT_USAGE_METRICS_CONFIG.rvm_credits.color,
                }}
              />
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiItemHeader}>
                  <Typography
                    variant={'body-rg-regular'}
                    color={'tertiary'}
                    ariaLabel={'RvmCreditsTitle'}
                  >
                    RVM credits
                  </Typography>
                </div>
                <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'RvmCreditsValue'}>
                  {ringlessVoicemailStat?.value?.toLocaleString() || '0'}
                </Typography>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <ChartFilter store={chartFilterStore} />
          </div>
          <div className={styles.chartArea}>
            {chartLoading ? (
              <SpinnerLoader ariaLabel={'CreditUsageAnalyticsPage'} />
            ) : (
              <>
                {chartFilterStore.chartType === 'line' && (
                  <CreditUsageLineChart
                    chartDataStore={chartDataStore}
                    hiddenMetrics={hiddenMetrics}
                  />
                )}
                {chartFilterStore.chartType === 'bar' && (
                  <CreditUsageBarChart
                    chartDataStore={chartDataStore}
                    hiddenMetrics={hiddenMetrics}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <FullPageWrapper className={styles.container}>
      <div>
        <AnalyticsHeader title='Credit Usage' actions={exportActions} />
        <div className={styles.filtersRow}>
          <CalendarDropdown store={calendarFilterStore} />
        </div>
        {renderContent()}
        <CreditUsageTable />
      </div>
      {contactsDetailsStore && <ContactsDetails store={contactsDetailsStore} />}
    </FullPageWrapper>
  )
})

export const CreditUsageAnalyticsPage = () => (
  <CreditUsageAnalyticsPageProvider>
    <CreditUsageAnalyticsPageContent />
  </CreditUsageAnalyticsPageProvider>
)
